import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Stethoscope,
  Video,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  PlayCircle,
  Wand2,
  FileText,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Video,
      title: "No Camera. No Studio.",
      description:
        "Upload one face video and voice sample once. Generate unlimited videos from any script.",
    },
    {
      icon: Shield,
      title: "Compliant by Default",
      description:
        "India, UAE & USA medical-marketing rules baked into every script — auto-fixed before you see it.",
    },
    {
      icon: Wand2,
      title: "Editor-Polished Output",
      description:
        "Auto-subtitles, real Pexels B-roll, hook animations, and your clinic branding overlay.",
    },
    {
      icon: Sparkles,
      title: "Ideas → Script → Video",
      description:
        "Tell us your specialty. We generate ideas, write scripts, and turn them into shoot-free videos.",
    },
    {
      icon: Zap,
      title: "Batch a Month in an Hour",
      description:
        "Generate 10 ideas, 10 scripts, 10 avatar videos in one session. Schedule and forget.",
    },
    {
      icon: CheckCircle,
      title: "Done-For-You Available",
      description:
        "Want us to handle posting, editing, and growth? Our managed service has you covered.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span
              className="text-2xl font-bold text-[#2C5F44]"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Doctor Content Engine
            </span>
          </div>
          <Button
            onClick={() => navigate("/auth")}
            className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-6"
            data-testid="header-get-started-btn"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D98A6C]/10 text-[#D98A6C] text-xs font-bold rounded-full mb-5 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              AI Avatar · Compliant · Ready to Post
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C2A21] mb-6 tracking-tight"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Create doctor videos
              <span className="text-[#2C5F44]"> without shooting</span>.
            </h1>
            <p
              className="text-lg text-[#5C7063] mb-8"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              AI-generated, compliant, ready-to-post videos tailored to your
              country. Upload your face once. Generate from any script in one
              click — for life.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/auth")}
                className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-8 py-6 text-lg"
                data-testid="hero-get-started-btn"
              >
                Create Video Without Shooting
                <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
              </Button>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[#2C5F44] hover:bg-[#2C5F44]/5 font-medium"
                data-testid="hero-watch-demo-link"
              >
                <PlayCircle className="w-5 h-5" strokeWidth={1.5} />
                See it work
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-[#5C7063]">
              <div className="flex items-center gap-2">
                <CheckCircle
                  className="w-4 h-4 text-[#2C5F44]"
                  strokeWidth={2}
                />
                No camera needed
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle
                  className="w-4 h-4 text-[#2C5F44]"
                  strokeWidth={2}
                />
                Compliance auto-fixed
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle
                  className="w-4 h-4 text-[#2C5F44]"
                  strokeWidth={2}
                />
                Multi-country
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-4 shadow-[0_8px_30px_rgba(44,95,68,0.06)] border border-[#2C5F44]/10">
              <img
                src="https://images.pexels.com/photos/6749773/pexels-photo-6749773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Doctor"
                className="w-full h-auto rounded-2xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-white px-4 py-2 rounded-2xl border border-[#2C5F44]/10 shadow-md flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-[#1C2A21]">
                  Generated by AI · Not filmed
                </span>
              </div>
              <div className="absolute -top-4 -right-4 bg-[#D98A6C] text-white px-4 py-2 rounded-2xl shadow-md flex items-center gap-2">
                <Sparkles className="w-4 h-4" strokeWidth={2} />
                <span className="text-xs font-semibold">India compliant</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Demo strip: Script → Avatar → Final Video */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1C2A21] mb-4"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            From script to ready-to-post in 3 steps
          </h2>
          <p className="text-lg text-[#5C7063] max-w-2xl mx-auto">
            No camera. No editing. No compliance worries.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              step: "01",
              title: "AI writes a compliant script",
              description:
                "Tell us your topic. We generate hook + explanation + CTA, auto-fixed for your country and platform.",
              icon: FileText,
              accent: "#2C5F44",
              demo: '"Did you know 7 in 10 cholesterol myths are wrong? Here\'s what actually matters…"',
            },
            {
              step: "02",
              title: "Your AI avatar delivers it",
              description:
                "Uploaded once, used forever. Your face & voice generate the video — no recording session.",
              icon: Video,
              accent: "#D98A6C",
              demo: "Avatar lip-syncs your script · 9:16 vertical · ready in seconds",
            },
            {
              step: "03",
              title: "Polished, branded, ready",
              description:
                "Auto-subtitles, hook animation, real B-roll from Pexels, and your clinic branding overlay.",
              icon: Wand2,
              accent: "#2C5F44",
              demo: "Captions + B-roll + watermark + .srt download",
            },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10 relative"
              data-testid={`landing-step-${i}`}
            >
              <div
                className="absolute -top-4 left-8 px-3 py-1 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: s.accent }}
              >
                STEP {s.step}
              </div>
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${s.accent}15`, color: s.accent }}
              >
                <s.icon className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h3
                className="text-xl font-bold text-[#1C2A21] mb-2"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                {s.title}
              </h3>
              <p className="text-[#5C7063] mb-4">{s.description}</p>
              <div
                className="bg-[#F4F1EA] rounded-xl px-4 py-3 text-sm text-[#1C2A21] italic border-l-2"
                style={{ borderColor: s.accent }}
              >
                {s.demo}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1C2A21] mb-4"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Built for doctors who hate filming
          </h2>
          <p className="text-lg text-[#5C7063] max-w-2xl mx-auto">
            We remove every excuse for not posting consistently.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
            >
              <div className="w-14 h-14 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-4">
                <feature.icon
                  className="w-7 h-7 text-[#2C5F44]"
                  strokeWidth={1.5}
                />
              </div>
              <h3
                className="text-xl font-semibold text-[#1C2A21] mb-3"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                {feature.title}
              </h3>
              <p className="text-[#5C7063]">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Success Stories */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1C2A21] mb-4"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Doctors growing without shooting
          </h2>
          <p className="text-lg text-[#5C7063] max-w-2xl mx-auto">
            Three avatars uploaded once. Hundreds of videos posted since.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              name: "Dr. Priya Sharma",
              specialty: "Dermatologist, Mumbai",
              image:
                "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzQzMzk3NTd8MA&ixlib=rb-4.1.0&q=85",
              testimonial:
                "I haven't filmed a single clip in 4 months. My AI avatar posts 3 times a week. Compliance is auto-handled. 15K followers, zero camera anxiety.",
              result: "15K followers · 0 shoots",
            },
            {
              name: "Dr. Ahmed Al-Mansouri",
              specialty: "Cardiologist, Dubai",
              image:
                "https://images.pexels.com/photos/6749773/pexels-photo-6749773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
              testimonial:
                "Uploaded my face video on a Sunday. Posted my first AI video on Monday. UAE compliance is built-in — I never worry about the regulator.",
              result: "3× consultations",
            },
            {
              name: "Dr. Sarah Johnson",
              specialty: "Pediatrician, New York",
              image:
                "https://images.pexels.com/photos/7659867/pexels-photo-7659867.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
              testimonial:
                "I write a script in the school pickup line, hit Generate, and it's posted by dinner. No editing, no studio. Parents love the consistency.",
              result: "Consistent 10+ posts/month",
            },
          ].map((story, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
            >
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={story.image}
                  alt={story.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h3
                    className="font-bold text-[#1C2A21]"
                    style={{ fontFamily: "Nunito, sans-serif" }}
                  >
                    {story.name}
                  </h3>
                  <p className="text-sm text-[#5C7063]">{story.specialty}</p>
                </div>
              </div>
              <p className="text-[#1C2A21] mb-4 italic">
                "{story.testimonial}"
              </p>
              <div className="pt-4 border-t border-[#2C5F44]/10">
                <span className="inline-block px-4 py-2 bg-[#82A08B]/20 text-[#2C5F44] rounded-full text-sm font-semibold">
                  {story.result}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-[#2C5F44] to-[#244b36] rounded-3xl p-12 text-center text-white relative overflow-hidden"
        >
          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#D98A6C]/20 blur-3xl" />
          <h2
            className="text-3xl sm:text-4xl font-bold mb-4 relative"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Stop filming. Start posting.
          </h2>
          <p className="text-lg mb-8 text-white/90 relative">
            One face video. One voice sample. Unlimited compliant doctor videos
            for life.
          </p>
          <Button
            onClick={() => navigate("/onboarding")}
            className="button-pill bg-white hover:bg-gray-100 text-[#2C5F44] rounded-full px-8 py-6 text-lg font-semibold relative"
            data-testid="cta-get-started-btn"
          >
            Create Video Without Shooting
            <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2C5F44]/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-[#5C7063]">
          <p>
            © 2026 Doctor Content Engine. Building trust through video — without
            the camera.
          </p>
        </div>
      </footer>
    </div>
  );
}
